import { faPlus, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { RoleReference } from '@supply-mover-2-0/core';
import clsx from 'clsx';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';

interface RoleSelectorProps {
	availableRoles: Array<RoleReference>;
	selectedRoleIds: Array<number>;
	onRolesChange: (roleIds: Array<number>) => void;
	disabled?: boolean;
}

/**
 * Role selector component for choosing which roles to share a saved search with.
 * Shows selected roles as removable chips with an "Add Role" dropdown.
 */
export function RoleSelector({
	availableRoles,
	selectedRoleIds,
	onRolesChange,
	disabled = false,
}: RoleSelectorProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Close dropdown on click outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current != null &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Get selected roles with details
	const selectedRoles = availableRoles.filter((role) =>
		selectedRoleIds.includes(role.id),
	);

	// Get unselected roles filtered by search
	const filteredUnselectedRoles = availableRoles
		.filter((role) => !selectedRoleIds.includes(role.id))
		.filter(
			(role) =>
				searchQuery.length === 0 ||
				role.name.toLowerCase().includes(searchQuery.toLowerCase()),
		);

	const handleAddRole = (roleId: number) => {
		onRolesChange([...selectedRoleIds, roleId]);
		setSearchQuery('');
		setIsDropdownOpen(false);
	};

	const handleRemoveRole = (roleId: number) => {
		onRolesChange(selectedRoleIds.filter((id) => id !== roleId));
	};

	const handleToggleDropdown = () => {
		if (!disabled) {
			setIsDropdownOpen(!isDropdownOpen);
			if (!isDropdownOpen) {
				setTimeout(() => inputRef.current?.focus(), 0);
			}
		}
	};

	return (
		<div className="relative" ref={dropdownRef}>
			{/* Selected roles as chips */}
			<div
				className={clsx(
					'flex flex-wrap gap-2 min-h-[36px] p-2 border rounded-lg',
					colors.classes.bgSecondary,
					colors.classes.borderDefault,
				)}
			>
				{selectedRoles.map((role) => (
					<span
						key={role.id}
						className={clsx(
							'inline-flex items-center gap-1 px-2 py-1 text-sm rounded-md',
							'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
						)}
					>
						{t(role.name)}
						{!disabled && (
							<button
								type="button"
								onClick={() => {
									handleRemoveRole(role.id);
								}}
								className="hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
								aria-label={t('Remove role')}
							>
								<FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
							</button>
						)}
					</span>
				))}

				{/* Add role button */}
				{!disabled && filteredUnselectedRoles.length > 0 && (
					<button
						type="button"
						onClick={handleToggleDropdown}
						className={clsx(
							'inline-flex items-center gap-1 px-2 py-1 text-sm',
							colors.classes.textMuted,
							'border border-dashed rounded-md',
							colors.classes.borderDefault,
							'hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500',
						)}
					>
						<FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
						{t('Add Role')}
					</button>
				)}

				{selectedRoles.length === 0 && !isDropdownOpen && (
					<span className={clsx('text-sm py-1', colors.classes.textMuted)}>
						{t('No roles selected')}
					</span>
				)}
			</div>

			{/* Dropdown for adding roles */}
			{isDropdownOpen && (
				<div
					className={clsx(
						'absolute z-10 mt-1 w-full border rounded-lg shadow-lg',
						colors.classes.bgPrimary,
						colors.classes.borderDefault,
					)}
				>
					{/* Search input */}
					<div className={clsx('p-2 border-b', colors.classes.borderDefault)}>
						<input
							ref={inputRef}
							type="text"
							value={searchQuery}
							onChange={(e) => {
								setSearchQuery(e.target.value);
							}}
							placeholder={t('Search roles...')}
							className={clsx(
								'w-full px-3 py-2 text-sm border rounded-md',
								colors.classes.bgSecondary,
								colors.classes.borderDefault,
								colors.classes.textPrimary,
								'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
								'placeholder:text-gray-400 dark:placeholder:text-gray-500',
							)}
						/>
					</div>

					{/* Role list */}
					<div className="max-h-48 overflow-y-auto">
						{filteredUnselectedRoles.length === 0 ? (
							<div
								className={clsx(
									'px-3 py-4 text-sm text-center',
									colors.classes.textMuted,
								)}
							>
								{searchQuery.length > 0
									? t('No matching roles')
									: t('All roles selected')}
							</div>
						) : (
							filteredUnselectedRoles.map((role) => (
								<button
									key={role.id}
									type="button"
									onClick={() => {
										handleAddRole(role.id);
									}}
									className={clsx(
										'w-full text-left px-3 py-2 text-sm',
										colors.classes.textPrimary,
										'hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700',
										'focus:outline-none',
									)}
								>
									{t(role.name)}
								</button>
							))
						)}
					</div>
				</div>
			)}
		</div>
	);
}
